import { createContext, useContext, useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { secureStorage } from '../utils/secureStorage';

const AudioContext = createContext(null);

export const useAudio = () => {
  const context = useContext(AudioContext);
  if (!context) {
    throw new Error('useAudio must be used within AudioProvider');
  }
  return context;
};

const SKIP_LIMIT = 3;
const SKIP_RESET_MINUTES = 15; // Changed from 10 to 15 minutes
const PLAY_COUNT_THRESHOLD = 60; // Seconds required for a play to count
const TRACK_PREVIEW_DURATION = 30; // Preview limit for unpaid tracks
const BEAT_PREVIEW_DURATION = 60; // Preview limit for beats (60 seconds)
const VIDEO_PREVIEW_DURATION = 20; // Preview limit for unpaid videos

export const AudioProvider = ({ children }) => {
  const audioRef = useRef(new Audio());
  const [currentTrack, setCurrentTrack] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.7);
  const [isMuted, setIsMuted] = useState(false);
  const [isShuffle, setIsShuffle] = useState(false);
  const [repeatMode, setRepeatMode] = useState('none'); // none, one, all
  const [queue, setQueue] = useState([]);
  const [queueIndex, setQueueIndex] = useState(-1);
  
  // Radio mode state
  const [isRadioMode, setIsRadioMode] = useState(false);
  const [radioTracks, setRadioTracks] = useState([]);
  const [radioIndex, setRadioIndex] = useState(0);
  const [skipsRemaining, setSkipsRemaining] = useState(SKIP_LIMIT);
  const [skipResetTime, setSkipResetTime] = useState(null);
  const [radioHistory, setRadioHistory] = useState([]); // Track history for VIP users
  
  // Radio error recovery
  const consecutiveErrorsRef = useRef(0);
  const MAX_CONSECUTIVE_ERRORS = 5;
  
  // VIP status - fetched from user data
  const [isVIP, setIsVIP] = useState(false);
  
  // Admin status - admins bypass all previews
  const [isAdminUser, setIsAdminUser] = useState(false);
  
  // Play tracking state
  const [playStartTime, setPlayStartTime] = useState(0);
  const [totalPlayTime, setTotalPlayTime] = useState(0);
  const [hasRecordedPlay, setHasRecordedPlay] = useState(false);
  
  // Preview enforcement
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [showBuyPrompt, setShowBuyPrompt] = useState(false);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const API = `${BACKEND_URL}/api`;

  // Get auth token from localStorage
  const getAuthToken = () => {
    const authData = secureStorage.getJSON('auth');
    if (authData?.token) return authData.token;
    return secureStorage.getItem('token');
  };

  // Check VIP status (includes admin check - admins get lifetime VIP)
  const checkVIPStatus = async () => {
    try {
      const token = getAuthToken();
      if (!token) {
        setIsVIP(false);
        return;
      }
      
      // First check if user is admin (admins get lifetime VIP)
      const meResponse = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (meResponse.data?.is_admin) {
        setIsVIP(true); // Admins get lifetime VIP access
        setIsAdminUser(true);
        return;
      }
      
      // Otherwise check VIP subscription status
      const response = await axios.get(`${API}/vip/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIsVIP(response.data.is_vip && response.data.is_active);
    } catch (error) {
      setIsVIP(false);
    }
  };

  // Check VIP status on mount and when token changes
  useEffect(() => {
    checkVIPStatus();
    // iter_474 — Re-check periodically, but skip when tab is hidden.
    // (VIP status changes are rare enough that we don't need the
    // 60s wake-up while the user is on another tab.)
    const interval = setInterval(() => {
      if (document.hidden) return;
      checkVIPStatus();
    }, 60000);
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: mount-only fetch / stable closure
  }, []);

  // Record a play to the backend
  const recordPlay = async (contentType, contentId) => {
    try {
      const token = getAuthToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      // Record play for analytics
      await axios.post(`${API}/record-play`, {
        content_type: contentType,
        content_id: contentId,
        play_duration: PLAY_COUNT_THRESHOLD
      }, { headers });
      
      // Also record stream for leaderboard counting
      const streamRes = await axios.post(`${API}/stream/record`, {
        content_type: contentType,
        content_id: contentId,
        duration_played: PLAY_COUNT_THRESHOLD
      }, { headers });
      
      // If stream counted, update play_count on current track so UI refreshes
      if (streamRes.data?.counted && currentTrackRef.current?.id === contentId) {
        setCurrentTrack(prev => prev ? { ...prev, play_count: (prev.play_count || 0) + 1 } : prev);
      }
    } catch (error) {
      console.error('Failed to record play:', error);
    }
  };

  // ── Paid To Listen — full-play earner ────────────────────────────────
  // Fires when the user finishes an entire track/beat/AI track without
  // forward-seeking. The server validates enrollment, payout handles,
  // monthly cap, and once-per-content-per-month dedupe.
  const ptlCappedToastShownRef = useRef(false);
  const recordPtlPlay = async (contentType, contentId, durationSeconds, playedSeconds, forwardSeeks) => {
    try {
      const token = getAuthToken();
      if (!token) return;
      const headers = { Authorization: `Bearer ${token}` };
      const { data } = await axios.post(`${API}/ptl/record-play`, {
        content_id: contentId,
        content_type: contentType,
        duration_seconds: durationSeconds,
        played_seconds: playedSeconds,
        forward_seeks: forwardSeeks,
      }, { headers });
      if (data?.earned) {
        // Fire the global earnings-pop event — globally-mounted
        // <PtlEarningsPop /> animates a tiny confetti chip in the
        // bottom-right. Replaces the prior generic toast for a punchier
        // dopamine moment that doesn't push other toasts off screen.
        try {
          window.dispatchEvent(new CustomEvent('ptl:earned', {
            detail: { amount: Number(data.amount), kind: 'play' },
          }));
        } catch (_) { /* ignore */ }
        ptlCappedToastShownRef.current = false;
      } else if (data?.capped && !ptlCappedToastShownRef.current) {
        ptlCappedToastShownRef.current = true;
        // Cap is silently honored on the server; no toast — user doesn't
        // need to know there's a monthly cap. Listening still counts
        // toward leaderboards.
      }
    } catch (_) {
      // Silently ignore — PTL is best-effort, doesn't block playback.
    }
  };
  const recordPtlPlayRef = useRef(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: mount-only fetch / stable closure
  useEffect(() => { recordPtlPlayRef.current = recordPtlPlay; }, []);

  // Per-track forward-seek count. Reset on every track change.
  const forwardSeeksRef = useRef(0);

  // Assign to ref so event handlers can access it
  // (This will be done after recordPlayRef is declared)

  // Load skip data from localStorage on mount
  useEffect(() => {
    const savedSkips = localStorage.getItem('freePlayerSkips');
    const savedResetTime = localStorage.getItem('freePlayerSkipReset');
    
    if (savedSkips && savedResetTime) {
      const resetTime = new Date(savedResetTime);
      if (new Date() > resetTime) {
        setSkipsRemaining(SKIP_LIMIT);
        secureStorage.removeItem('freePlayerSkips');
        secureStorage.removeItem('freePlayerSkipReset');
      } else {
        setSkipsRemaining(parseInt(savedSkips));
        setSkipResetTime(resetTime);
      }
    }
  }, []);

  // Store dependencies in refs to avoid re-adding listeners
  const isPlayingRef = useRef(isPlaying);
  const currentTrackRef = useRef(currentTrack);
  const hasRecordedPlayRef = useRef(hasRecordedPlay);
  const totalPlayTimeRef = useRef(totalPlayTime);
  const isPreviewModeRef = useRef(isPreviewMode);
  const isRadioModeRef = useRef(isRadioMode);
  const repeatModeRef = useRef(repeatMode);
  const radioTracksRef = useRef(radioTracks);
  const radioIndexRef = useRef(radioIndex);
  const queueRef = useRef(queue);
  const queueIndexRef = useRef(queueIndex);
  const isShuffleRef = useRef(isShuffle);
  
  // Update refs when values change
  useEffect(() => { isPlayingRef.current = isPlaying; }, [isPlaying]);
  useEffect(() => { currentTrackRef.current = currentTrack; }, [currentTrack]);
  useEffect(() => { hasRecordedPlayRef.current = hasRecordedPlay; }, [hasRecordedPlay]);
  useEffect(() => { totalPlayTimeRef.current = totalPlayTime; }, [totalPlayTime]);
  useEffect(() => { isPreviewModeRef.current = isPreviewMode; }, [isPreviewMode]);
  useEffect(() => { isRadioModeRef.current = isRadioMode; }, [isRadioMode]);
  useEffect(() => { repeatModeRef.current = repeatMode; }, [repeatMode]);
  useEffect(() => { radioTracksRef.current = radioTracks; }, [radioTracks]);
  useEffect(() => { radioIndexRef.current = radioIndex; }, [radioIndex]);
  useEffect(() => { queueRef.current = queue; }, [queue]);
  useEffect(() => { queueIndexRef.current = queueIndex; }, [queueIndex]);
  useEffect(() => { isShuffleRef.current = isShuffle; }, [isShuffle]);

  // Refs for functions that need to be called from event handlers
  const playNextRadioRef = useRef(null);
  const playNextRef = useRef(null);
  const recordPlayRef = useRef(null);
  const playPreviousRadioRef = useRef(null);
  const showBuyPromptRef = useRef(showBuyPrompt);
  const adPausingAudioRef = useRef(false);
  
  // Assign recordPlay to ref in useEffect
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: mount-only fetch / stable closure
  useEffect(() => { recordPlayRef.current = recordPlay; }, []);
  useEffect(() => { showBuyPromptRef.current = showBuyPrompt; }, [showBuyPrompt]);

  // Track when we last recorded playback time
  const lastPlayTimeRef = useRef(0);

  useEffect(() => {
    const audio = audioRef.current;
    
    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
      
      // Track cumulative play time for 60-second threshold using real elapsed time
      if (isPlayingRef.current && currentTrackRef.current && !hasRecordedPlayRef.current) {
        const now = Date.now() / 1000;
        if (lastPlayTimeRef.current > 0) {
          const elapsed = Math.min(now - lastPlayTimeRef.current, 2); // cap at 2s to avoid jumps
          const newTotalTime = totalPlayTimeRef.current + elapsed;
          setTotalPlayTime(newTotalTime);
          
          // Record play when threshold reached
          if (newTotalTime >= PLAY_COUNT_THRESHOLD) {
            setHasRecordedPlay(true);
            if (recordPlayRef.current) {
              const track = currentTrackRef.current;
              let contentType = 'track';
              if (track?.is_beat) contentType = 'beat';
              else if (track?.is_ai_content || track?.uploaded_by_ai_artist) contentType = 'ai_track';
              
              recordPlayRef.current(contentType, track.id);
            }
          }
        }
        lastPlayTimeRef.current = now;
      }
      
      // Preview enforcement - stop at preview limit for unpurchased content
      // NEVER enforce preview limits in radio mode - radio plays full tracks
      // Beats get 60 seconds, tracks get 30 seconds (only for non-radio playback)
      if (isPreviewModeRef.current && !isRadioModeRef.current) {
        const previewDuration = currentTrackRef.current?.is_beat ? BEAT_PREVIEW_DURATION : TRACK_PREVIEW_DURATION;
        const previewStartOffset = currentTrackRef.current?.preview_start || 0;
        const previewEndTime = previewStartOffset + previewDuration;
        if (audio.currentTime >= previewEndTime) {
          audio.pause();
          setIsPlaying(false);
          setShowBuyPrompt(true);
        }
      }
    };
    
    const handleLoadedMetadata = () => setDuration(audio.duration);
    const handlePlay = () => { setIsPlaying(true); lastPlayTimeRef.current = Date.now() / 1000; };
    const handlePause = () => {
      // Only set isPlaying to false if we're not in ad-pausing or seeking state
      if (!adPausingAudioRef.current) {
        setIsPlaying(false);
        lastPlayTimeRef.current = 0; // Reset so we don't count paused time
      }
    };
    const handleEnded = () => {
      // ── Paid To Listen full-play earning ─────────────────────────
      // Fire once per ended track. Server handles enrollment / cap /
      // duplicate dedupe, so we always send.
      try {
        const t = currentTrackRef.current;
        const dur = audio.duration;
        const played = audio.currentTime;
        const seeks = forwardSeeksRef.current || 0;
        if (
          t?.id &&
          Number.isFinite(dur) && dur >= 5 && dur <= 1800 &&
          Number.isFinite(played) && played >= dur - 5
        ) {
          let contentType = 'track';
          if (t?.is_beat) contentType = 'beat';
          else if (t?.is_ai_content || t?.uploaded_by_ai_artist) contentType = 'ai_track';
          else if (t?.is_album_track) contentType = 'album_track';
          if (recordPtlPlayRef.current) {
            recordPtlPlayRef.current(contentType, t.id, dur, played, seeks);
          }
        }
      } catch (_) { /* never block playback */ }

      if (isRadioModeRef.current) {
        // Auto-advance in radio mode
        if (playNextRadioRef.current) playNextRadioRef.current();
      } else if (repeatModeRef.current === 'one') {
        audio.currentTime = 0;
        audio.play().catch(() => setIsPlaying(false));
      } else {
        if (playNextRef.current) playNextRef.current();
      }
    };
    const handleError = (e) => {
      console.error('Audio error:', e);
      if (isRadioModeRef.current) {
        const tracks = radioTracksRef.current;
        const currentIndex = radioIndexRef.current;
        const failedTrack = tracks[currentIndex];
        const mediaError = audioRef.current?.error;
        const errorCode = mediaError?.code;
        
        // MEDIA_ERR_NETWORK (2) = recoverable on mobile (buffering, connection hiccup)
        // MEDIA_ERR_ABORTED (1) = user aborted or browser interrupted (also recoverable)
        const isRecoverable = errorCode === 1 || errorCode === 2;
        
        consecutiveErrorsRef.current += 1;
        
        if (consecutiveErrorsRef.current >= MAX_CONSECUTIVE_ERRORS) {
          console.warn('Radio: too many consecutive errors, stopping playback');
          consecutiveErrorsRef.current = 0;
          setIsPlaying(false);
          return;
        }
        
        if (isRecoverable && failedTrack?.filename) {
          // Retry the SAME track after a brief delay (don't remove it)
          console.log('Radio: retrying recoverable error for', failedTrack.title);
          setTimeout(() => {
            const audio = audioRef.current;
            audio.src = `${BACKEND_URL}/api/stream/track/${failedTrack.filename}`;
            audio.play()
              .then(() => { consecutiveErrorsRef.current = 0; })
              .catch(() => {});
          }, 1500);
          return;
        }
        
        // Fatal error (decode/unsupported): remove track and skip
        if (failedTrack && tracks.length > 1) {
          const newTracks = tracks.filter((_, i) => i !== currentIndex);
          setRadioTracks(newTracks);
          radioTracksRef.current = newTracks;
          const newIndex = currentIndex >= newTracks.length ? 0 : currentIndex;
          setRadioIndex(newIndex);
          radioIndexRef.current = newIndex;
          
          const nextTrack = newTracks[newIndex];
          if (nextTrack && nextTrack.filename) {
            const audio = audioRef.current;
            audio.src = `${BACKEND_URL}/api/stream/track/${nextTrack.filename}`;
            audio.play()
              .then(() => { consecutiveErrorsRef.current = 0; })
              .catch(() => {});
            setCurrentTrack(nextTrack);
            setIsPlaying(true);
          }
        } else {
          // Only 1 track left and it failed — stop
          setIsPlaying(false);
        }
      }
    };

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('error', handleError);

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('error', handleError);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: mount-only fetch / stable closure
  }, []); // Empty dependency array - listeners added once and use refs

  useEffect(() => {
    audioRef.current.volume = volume;
    audioRef.current.muted = isMuted;
  }, [volume, isMuted]);

  // Reset play tracking when track changes
  useEffect(() => {
    setTotalPlayTime(0);
    setHasRecordedPlay(false);
    setShowBuyPrompt(false);
    lastPlayTimeRef.current = 0;
    forwardSeeksRef.current = 0;
  }, [currentTrack?.id]);

  // ── Media Session API (CarPlay / Android Auto / Lock Screen) ──
  useEffect(() => {
    if (!('mediaSession' in navigator)) return;

    const ms = navigator.mediaSession;

    // Action handlers
    ms.setActionHandler('play', () => {
      if (showBuyPromptRef.current) return;
      audioRef.current.play().catch(() => setIsPlaying(false));
      setIsPlaying(true);
    });
    ms.setActionHandler('pause', () => {
      audioRef.current.pause();
      setIsPlaying(false);
    });
    ms.setActionHandler('previoustrack', () => {
      if (isRadioModeRef.current) {
        if (playPreviousRadioRef.current) playPreviousRadioRef.current();
      } else if (queueRef.current.length > 0) {
        const idx = queueIndexRef.current - 1;
        if (idx >= 0) {
          setQueueIndex(idx);
          const track = queueRef.current[idx];
          const audio = audioRef.current;
          audio.src = track.file_url
            ? (track.file_url.startsWith('http') ? track.file_url : `${BACKEND_URL}${track.file_url}`)
            : `${BACKEND_URL}/api/stream/track/${track.filename}`;
          audio.play().catch(() => setIsPlaying(false));
          setCurrentTrack(track);
          setIsPlaying(true);
        }
      }
    });
    ms.setActionHandler('nexttrack', () => {
      if (isRadioModeRef.current) {
        if (playNextRadioRef.current) playNextRadioRef.current();
      } else if (playNextRef.current) {
        playNextRef.current();
      }
    });
    ms.setActionHandler('seekbackward', (details) => {
      const offset = details.seekOffset || 10;
      const t = Math.max(audioRef.current.currentTime - offset, 0);
      audioRef.current.currentTime = t;
      setCurrentTime(t);
    });
    ms.setActionHandler('seekforward', (details) => {
      const offset = details.seekOffset || 10;
      const t = Math.min(audioRef.current.currentTime + offset, audioRef.current.duration || 0);
      forwardSeeksRef.current = (forwardSeeksRef.current || 0) + 1;
      audioRef.current.currentTime = t;
      setCurrentTime(t);
    });
    ms.setActionHandler('seekto', (details) => {
      if (details.seekTime != null) {
        if (details.seekTime > (audioRef.current.currentTime || 0) + 2) {
          forwardSeeksRef.current = (forwardSeeksRef.current || 0) + 1;
        }
        audioRef.current.currentTime = details.seekTime;
        setCurrentTime(details.seekTime);
      }
    });
    ms.setActionHandler('stop', () => {
      audioRef.current.pause();
      setIsPlaying(false);
      setCurrentTrack(null);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: mount-only fetch / stable closure
  }, []); // Set once on mount

  // Update Media Session metadata when track changes
  useEffect(() => {
    if (!('mediaSession' in navigator) || !currentTrack) return;

    const artworkUrl = currentTrack.artwork || currentTrack.cover_art || currentTrack.album_artwork;
    const artwork = artworkUrl
      ? [{ src: artworkUrl.startsWith('http') ? artworkUrl : `${BACKEND_URL}${artworkUrl}`, sizes: '512x512', type: 'image/jpeg' }]
      : [];

    navigator.mediaSession.metadata = new MediaMetadata({
      title: currentTrack.title || 'Unknown Track',
      artist: currentTrack.artist || currentTrack.username || 'Unknown Artist',
      album: currentTrack.album_title || (isRadioMode ? 'One Voice Radio' : 'One Voice Distro'),
      artwork,
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: mount-only fetch / stable closure
  }, [currentTrack?.id, currentTrack?.title, isRadioMode]);

  // Update Media Session playback state
  useEffect(() => {
    if (!('mediaSession' in navigator)) return;
    navigator.mediaSession.playbackState = isPlaying ? 'playing' : 'paused';
  }, [isPlaying]);

  // Update Media Session position state for seek bars (throttled to ~1/sec)
  const lastPositionUpdate = useRef(0);
  useEffect(() => {
    if (!('mediaSession' in navigator) || !duration || !navigator.mediaSession.setPositionState) return;
    const sec = Math.floor(currentTime);
    if (sec === lastPositionUpdate.current) return;
    lastPositionUpdate.current = sec;
    try {
      navigator.mediaSession.setPositionState({
        duration: duration || 0,
        playbackRate: audioRef.current.playbackRate || 1,
        position: Math.min(currentTime, duration),
      });
    } catch (e) { /* ignore invalid state errors */ }
  }, [currentTime, duration]);

  // Regular track playback
  const playTrack = (track, trackList = [], options = {}) => {
    // Exit radio mode when playing a regular track
    setIsRadioMode(false);
    
    // Check if this is a preview (user hasn't purchased and doesn't own)
    const isOwner = options.isOwner || false;
    const hasPurchased = options.hasPurchased || false;
    const isFreeTrack = track.is_free || false;
    const previewOnly = !isOwner && !hasPurchased && !isFreeTrack && !isAdminUser;
    
    setIsPreviewMode(previewOnly);
    setShowBuyPrompt(false);
    
    const audio = audioRef.current;
    // Support custom file_url (e.g., DJ mixes) or standard track streaming.
    // If the track row lacks a `filename`, fall back to the track-by-id
    // endpoint so the album-detail Play button works even when the
    // legacy /albums/{id}/tracks payload omits filename.
    //
    // HTML5 <audio> can't send Authorization headers, so we append the
    // JWT as a `?token=` query param. The backend stream endpoint
    // accepts it as a fallback; this is required for the uploader (and
    // VIPs/buyers) to bypass the 24h VIP wait window on freshly-uploaded
    // tracks. Without it, anonymous-looking stream requests get 403'd
    // and the browser shows "Couldn't load this track".
    //
    // IMPORTANT: tokens are kept in sessionStorage (see utils/secureStorage.js),
    // not localStorage — using the wrong store returns null and re-introduces
    // the 403 bug.
    const authToken = secureStorage.getItem('token');
    const tokenParam = authToken ? `?token=${encodeURIComponent(authToken)}` : '';

    if (track.file_url) {
      const baseUrl = track.file_url.startsWith('http') ? track.file_url : `${BACKEND_URL}${track.file_url}`;
      audio.src = authToken ? `${baseUrl}${baseUrl.includes('?') ? '&' : '?'}token=${encodeURIComponent(authToken)}` : baseUrl;
    } else if (track.filename) {
      audio.src = `${BACKEND_URL}/api/stream/track/${track.filename}${tokenParam}`;
    } else {
      audio.src = `${BACKEND_URL}/api/stream/track-by-id/${track.id}${tokenParam}`;
    }

    // Ensure the browser starts loading the new source
    // Note: setting audio.src already triggers loading, explicit load() not needed

    // For preview mode with a preview_start offset, seek after metadata loads
    const previewStart = previewOnly ? (track.preview_start || 0) : 0;
    if (previewStart > 0) {
      const seekOnLoad = () => {
        audio.currentTime = previewStart;
        audio.removeEventListener('loadedmetadata', seekOnLoad);
      };
      audio.addEventListener('loadedmetadata', seekOnLoad);
    }

    audio.play().catch((err) => {
      setIsPlaying(false);
      // Surface a one-time toast so users on production know why nothing
      // plays — but DO NOT mislead them. iOS Safari fires
      // NotSupportedError when the server returns ANY non-audio response
      // (e.g., a 403 page from a gate), not when the actual codec is
      // unsupported. So we map both NotSupported and generic errors to
      // the same "couldn't load" message and let NotAllowed (the actual
      // autoplay block) stay specific.
      try {
        import('sonner').then(({ toast }) => {
          if (err?.name === 'NotAllowedError') {
            toast.info('Tap the play button again to start playback (mobile autoplay block).');
          } else {
            // Don't spam users with the same toast 5x if a queue fails —
            // sonner dedupes on `id`.
            toast.error("Couldn't load this track — please try again.", {
              id: 'playback-failed',
              duration: 3500,
            });
          }
        });
      } catch (_e) { /* sonner unavailable in some test envs — ignore */ }
    });
    setCurrentTrack(track);
    setIsPlaying(true);
    
    if (trackList.length > 0) {
      setQueue(trackList);
      const idx = trackList.findIndex(t => t.id === track.id);
      setQueueIndex(idx >= 0 ? idx : 0);
    }
  };

  // Radio mode playback
  const startRadio = (tracks, startIndex = 0) => {
    if (tracks.length === 0) return;
    
    // Filter out tracks without filename
    const validTracks = tracks.filter(t => t && t.filename);
    if (validTracks.length === 0) return;
    
    // Recalculate start index if it changed after filtering
    let adjustedIndex = startIndex;
    if (adjustedIndex >= validTracks.length) adjustedIndex = 0;
    
    setIsRadioMode(true);
    setIsPreviewMode(false); // Radio NEVER uses preview mode - full tracks always
    setShowBuyPrompt(false);
    setRadioTracks(validTracks);
    setRadioIndex(adjustedIndex);
    consecutiveErrorsRef.current = 0;
    
    // Also update refs immediately so handleTimeUpdate doesn't race
    isPreviewModeRef.current = false;
    isRadioModeRef.current = true;
    
    const track = validTracks[adjustedIndex];
    const audio = audioRef.current;
    audio.src = `${BACKEND_URL}/api/stream/track/${track.filename}`;
    audio.play()
      .then(() => {
        consecutiveErrorsRef.current = 0;
      })
      .catch((err) => {
        console.warn('Radio: initial play failed', err?.message);
        // Try next track
        setTimeout(() => playNextRadio(true), 1000);
      });
    setCurrentTrack(track);
    setIsPlaying(true);
  };

  const playNextRadio = (retryFromError = false) => {
    const tracks = radioTracksRef.current;
    const currentIndex = radioIndexRef.current;
    
    if (tracks.length === 0) return;
    
    // Ensure preview mode is always off for radio
    setIsPreviewMode(false);
    setShowBuyPrompt(false);
    
    // Track consecutive errors to prevent infinite loops
    if (retryFromError) {
      consecutiveErrorsRef.current += 1;
      if (consecutiveErrorsRef.current >= MAX_CONSECUTIVE_ERRORS) {
        console.warn('Radio: too many consecutive errors, stopping');
        consecutiveErrorsRef.current = 0;
        setIsPlaying(false);
        return;
      }
    } else {
      consecutiveErrorsRef.current = 0;
    }
    
    const nextIndex = (currentIndex + 1) % tracks.length;
    setRadioIndex(nextIndex);
    
    const track = tracks[nextIndex];
    
    // Remove tracks without a filename from the queue entirely (don't just skip)
    if (!track || !track.filename) {
      console.warn('Radio: removing track without filename from queue', track?.id);
      const newTracks = tracks.filter((_, i) => i !== nextIndex);
      setRadioTracks(newTracks);
      radioTracksRef.current = newTracks;
      if (newTracks.length === 0) {
        setIsPlaying(false);
        return;
      }
      // Adjust index and continue (don't count as error)
      const adjustedIdx = nextIndex >= newTracks.length ? 0 : nextIndex;
      setRadioIndex(adjustedIdx);
      radioIndexRef.current = adjustedIdx;
      setTimeout(() => playNextRadio(false), 50);
      return;
    }
    
    const audio = audioRef.current;
    audio.src = `${BACKEND_URL}/api/stream/track/${track.filename}`;
    
    audio.play()
      .then(() => {
        consecutiveErrorsRef.current = 0; // Reset on success
      })
      .catch((err) => {
        console.warn('Radio: play failed for', track.title, err?.message);
        // Check if this is an autoplay policy rejection (NotAllowedError)
        if (err?.name === 'NotAllowedError') {
          // Don't auto-advance - user needs to interact. Just update state.
          setIsPlaying(false);
          return;
        }
        // For other errors, auto-advance to next track after a short delay
        setTimeout(() => playNextRadio(true), 500);
      });
    setCurrentTrack(track);
    setIsPlaying(true);
  };

  // Assign to ref so event handlers can access it
  useEffect(() => { playNextRadioRef.current = playNextRadio; });

  const skipRadio = () => {
    // VIP users get unlimited skips
    if (!isVIP) {
      if (skipsRemaining <= 0) {
        return false;
      }
      
      const newSkips = skipsRemaining - 1;
      setSkipsRemaining(newSkips);
      
      // Set reset time if this is the first skip
      if (!skipResetTime) {
        const resetTime = new Date(Date.now() + SKIP_RESET_MINUTES * 60 * 1000);
        setSkipResetTime(resetTime);
        secureStorage.setItem('freePlayerSkipReset', resetTime.toISOString());
      }
      secureStorage.setItem('freePlayerSkips', newSkips.toString());
    }
    
    // Add current track to history before skipping (for VIP users)
    if (currentTrack && isVIP) {
      setRadioHistory(prev => {
        const newHistory = [...prev, currentTrack];
        // Keep only last 50 tracks
        return newHistory.slice(-50);
      });
    }
    
    playNextRadio();
    return true;
  };

  // VIP feature: Play previous track from radio history
  // Returns: { success: boolean, blocked: boolean } - blocked means VIP required
  const playPreviousRadio = (forceCheck = false) => {
    // If forceCheck is true, just check if action is allowed without executing
    if (forceCheck) {
      return { success: false, blocked: !isVIP };
    }
    
    if (!isVIP) {
      return { success: false, blocked: true };
    }
    
    if (radioHistory.length === 0) {
      return { success: false, blocked: false };
    }
    
    const previousTrack = radioHistory[radioHistory.length - 1];
    setRadioHistory(prev => prev.slice(0, -1));
    
    if (previousTrack) {
      setCurrentTrack(previousTrack);
      audioRef.current.src = previousTrack.stream_url || `${API}/stream/${previousTrack.id}`;
      audioRef.current.play().catch(() => setIsPlaying(false));
      setIsPlaying(true);
      return { success: true, blocked: false };
    }
    return { success: false, blocked: false };
  };

  // Assign playPreviousRadio to ref for Media Session handler
  useEffect(() => { playPreviousRadioRef.current = playPreviousRadio; });

  // VIP feature: Fast forward 10 seconds
  // Returns: { success: boolean, blocked: boolean } - blocked means VIP required
  const fastForward = (forceCheck = false) => {
    if (forceCheck) {
      return { success: false, blocked: !isVIP };
    }
    
    if (!isVIP) {
      return { success: false, blocked: true };
    }
    
    const newTime = Math.min(audioRef.current.currentTime + 10, duration);
    forwardSeeksRef.current = (forwardSeeksRef.current || 0) + 1;
    audioRef.current.currentTime = newTime;
    setCurrentTime(newTime);
    return { success: true, blocked: false };
  };

  // VIP feature: Rewind 10 seconds
  // Returns: { success: boolean, blocked: boolean } - blocked means VIP required
  const rewind = (forceCheck = false) => {
    if (forceCheck) {
      return { success: false, blocked: !isVIP };
    }
    
    if (!isVIP) {
      return { success: false, blocked: true };
    }
    
    const newTime = Math.max(audioRef.current.currentTime - 10, 0);
    audioRef.current.currentTime = newTime;
    setCurrentTime(newTime);
    return { success: true, blocked: false };
  };

  const stopRadio = () => {
    setIsRadioMode(false);
    audioRef.current.pause();
    setIsPlaying(false);
    setCurrentTrack(null);
  };

  // Close the player completely (for X button)
  const closePlayer = () => {
    audioRef.current.pause();
    setIsPlaying(false);
    setCurrentTrack(null);
    setIsRadioMode(false);
    setIsPreviewMode(false);
    setShowBuyPrompt(false);
    setQueue([]);
    setQueueIndex(0);
  };

  const togglePlay = () => {
    const audio = audioRef.current;
    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      // Don't resume if buy prompt is showing
      if (showBuyPrompt) return;
      audio.play().catch(() => setIsPlaying(false));
      setIsPlaying(true);
    }
  };

  // Explicit pause/resume (non-toggle) for ad interruptions
  const pauseAudio = () => {
    adPausingAudioRef.current = true;
    audioRef.current.pause();
    setIsPlaying(false);
    // Reset after a tick so subsequent external pause events are caught
    setTimeout(() => { adPausingAudioRef.current = false; }, 100);
  };

  const resumeAudio = () => {
    if (showBuyPrompt) return;
    audioRef.current.play().catch(() => setIsPlaying(false));
    setIsPlaying(true);
  };

  const seek = (time) => {
    // Don't allow seeking past preview limit
    if (isPreviewMode) {
      const previewDuration = currentTrack?.is_beat ? BEAT_PREVIEW_DURATION : TRACK_PREVIEW_DURATION;
      const previewStartOffset = currentTrack?.preview_start || 0;
      if (time >= previewStartOffset + previewDuration) {
        setShowBuyPrompt(true);
        return;
      }
    }
    // Track meaningful forward seeks for Paid-To-Listen anti-farming
    try {
      const prev = audioRef.current?.currentTime || 0;
      if (time > prev + 2) forwardSeeksRef.current = (forwardSeeksRef.current || 0) + 1;
    } catch (_) {}
    audioRef.current.currentTime = time;
    setCurrentTime(time);
  };

  const playNext = () => {
    if (isRadioMode) {
      playNextRadio();
      return;
    }
    
    if (queue.length === 0) return;
    
    let nextIndex;
    if (isShuffle) {
      nextIndex = Math.floor(Math.random() * queue.length);
    } else {
      nextIndex = queueIndex + 1;
      if (nextIndex >= queue.length) {
        if (repeatMode === 'all') {
          nextIndex = 0;
        } else {
          setIsPlaying(false);
          return;
        }
      }
    }
    
    setQueueIndex(nextIndex);
    playTrack(queue[nextIndex], queue);
  };

  // Assign to ref so event handlers can access it
  useEffect(() => { playNextRef.current = playNext; });

  const playPrevious = () => {
    // VIP users can go back in radio mode
    if (isRadioMode) {
      if (isVIP && radioHistory.length > 0) {
        playPreviousRadio();
      }
      return;
    }
    
    if (queue.length === 0) return;
    
    if (currentTime > 3) {
      seek(0);
      return;
    }
    
    let prevIndex = queueIndex - 1;
    if (prevIndex < 0) {
      prevIndex = repeatMode === 'all' ? queue.length - 1 : 0;
    }
    
    setQueueIndex(prevIndex);
    playTrack(queue[prevIndex], queue);
  };

  const toggleShuffle = () => setIsShuffle(!isShuffle);
  
  const toggleRepeat = () => {
    const modes = ['none', 'all', 'one'];
    const currentIndex = modes.indexOf(repeatMode);
    setRepeatMode(modes[(currentIndex + 1) % modes.length]);
  };
  
  const dismissBuyPrompt = () => {
    setShowBuyPrompt(false);
  };

  const toggleMute = () => {
    setIsMuted(prev => !prev);
  };

  const value = {
    currentTrack,
    isPlaying,
    currentTime,
    duration,
    volume,
    isMuted,
    isShuffle,
    repeatMode,
    queue,
    playTrack,
    togglePlay,
    pauseAudio,
    resumeAudio,
    seek,
    setVolume,
    toggleMute,
    playNext,
    playPrevious,
    toggleShuffle,
    toggleRepeat,
    closePlayer, // Close/dismiss the player
    // Radio mode exports
    isRadioMode,
    radioTracks,
    radioIndex,
    skipsRemaining,
    skipResetTime,
    startRadio,
    skipRadio,
    stopRadio,
    // VIP features
    isVIP,
    radioHistory,
    playPreviousRadio,
    fastForward,
    rewind,
    // Preview mode exports
    isPreviewMode,
    showBuyPrompt,
    dismissBuyPrompt,
    TRACK_PREVIEW_DURATION,
    BEAT_PREVIEW_DURATION,
  };

  return (
    <AudioContext.Provider value={value}>
      {children}
    </AudioContext.Provider>
  );
};
